
import { useState } from "react";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Loader2, AlertCircle, InfoIcon, ExternalLink, Globe, Search, GitBranch } from "lucide-react";
import { fetchPostsFromWordPressApi } from "@/utils/wordpress/api";
import { BlogPost } from "@/types/blog";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

interface ImportFormProps {
  onImportComplete: (posts: BlogPost[]) => void;
  isLoading: boolean;
}

export const ImportForm = ({ onImportComplete, isLoading }: ImportFormProps) => {
  const [wpApiUrl, setWpApiUrl] = useState<string>("https://edgefi.com");
  const [importing, setImporting] = useState<boolean>(false);
  const [importProgress, setImportProgress] = useState<string>("");
  const [progressPercent, setProgressPercent] = useState<number>(0);
  const [importError, setImportError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("import");
  const [suggestedUrls, setSuggestedUrls] = useState<string[]>([]);
  
  // Generate alternative URL suggestions based on the entered URL
  const generateUrlSuggestions = (baseUrl: string): string[] => {
    try {
      // Clean up the URL
      let domain = baseUrl.trim();
      
      // Remove protocol
      domain = domain.replace(/^https?:\/\//, '');
      
      // Remove paths
      domain = domain.split('/')[0];
      
      // Remove query parameters and anchors
      domain = domain.split('?')[0].split('#')[0];
      
      // Generate variations
      const variations = [
        `https://${domain}`,
        `https://${domain}/blog`,
      ];
      
      // Add www version if not present, or remove www if present
      if (domain.startsWith('www.')) {
        variations.push(`https://${domain.substring(4)}`);
        variations.push(`https://${domain.substring(4)}/blog`);
      } else {
        variations.push(`https://www.${domain}`);
        variations.push(`https://www.${domain}/blog`);
      }
      
      // Filter out the original URL
      return [...new Set(variations)].filter(url => url.toLowerCase() !== baseUrl.toLowerCase());
    } catch (error) {
      console.error("Error generating URL suggestions:", error);
      return [];
    }
  };

  const handleImport = async () => {
    try {
      if (!wpApiUrl) {
        toast({
          title: "Error",
          description: "Please enter a WordPress site URL",
          variant: "destructive"
        });
        return;
      }
      
      // Reset state
      setImporting(true);
      setImportProgress("Initializing import...");
      setProgressPercent(5);
      setImportError(null);
      setErrorDetails(null);
      setSuggestedUrls([]);
      
      // Clean up the URL
      let cleanUrl = wpApiUrl.trim();
      
      // Remove trailing slashes
      while (cleanUrl.endsWith('/')) {
        cleanUrl = cleanUrl.slice(0, -1);
      }
      
      // Enforce HTTPS protocol - ALWAYS use HTTPS
      if (!cleanUrl.startsWith('https://')) {
        if (cleanUrl.startsWith('http://')) {
          cleanUrl = `https://${cleanUrl.substring(7)}`;
          console.log("Converted HTTP to HTTPS URL:", cleanUrl);
        } else {
          cleanUrl = `https://${cleanUrl}`;
        }
        console.log("Using HTTPS URL:", cleanUrl);
      }
      
      // Fetch the posts
      toast({
        title: "Starting WordPress Import",
        description: "Connecting to WordPress API...",
      });
      
      console.log("Fetching posts from:", cleanUrl);
      
      // Set up a progress callback
      const progressCallback = (message: string) => {
        setImportProgress(message);
        
        // Try to extract percentage information from progress messages
        const pageMatch = message.match(/Fetching page (\d+) of (\d+)/);
        if (pageMatch && pageMatch.length >= 3) {
          const currentPage = parseInt(pageMatch[1]);
          const totalPages = parseInt(pageMatch[2]);
          if (!isNaN(currentPage) && !isNaN(totalPages) && totalPages > 0) {
            setProgressPercent(Math.min(95, Math.floor((currentPage / totalPages) * 90) + 5));
          }
        }
        
        if (message.startsWith("Successfully fetched")) {
          setProgressPercent(100);
        }
      };
      
      try {
        // Try to verify the WordPress API before starting the full import
        console.log("Verifying WordPress API endpoint first...");
        setImportProgress("Verifying WordPress API endpoint...");
        
        const posts = await fetchPostsFromWordPressApi(cleanUrl, progressCallback);
        console.log("Fetched posts:", posts.length);
        
        // Log detailed information about the first few posts
        posts.slice(0, 5).forEach((post, index) => {
          console.log(`Post ${index + 1}: ${post.title}`);
          console.log(`  - Slug: ${post.slug}`);
          console.log(`  - WordPress ID: ${post.wordpressId}`);
          console.log(`  - Author: ${post.author || 'Unknown'}`);
          console.log(`  - Date: ${post.date} (Original: ${post.originalDate})`);
          console.log(`  - Tags: ${post.tags?.join(', ')}`);
        });
        
        // Pass the posts to the parent component
        onImportComplete(posts);
        
        toast({
          title: "Import Complete",
          description: `Successfully fetched ${posts.length} posts from WordPress.`,
        });
      } catch (error) {
        throw error; // Re-throw to be handled by outer catch block
      }
    } catch (error) {
      console.error("Import failed:", error);
      setProgressPercent(0);
      
      // Extract and display useful error information
      let errorMessage = "Unknown error occurred";
      let details = "";
      
      if (error instanceof Error) {
        errorMessage = error.message;
        
        // Special handling for HTML responses - this is a common issue
        const isHtmlError = error.message.includes('<!doctype') || 
                            error.message.includes('<html') || 
                            error.message.includes('HTML instead of JSON') ||
                            error.message.includes('not valid JSON');
        
        if (isHtmlError) {
          // Switch to the troubleshooting tab
          setActiveTab("troubleshooting");
          
          // Domain-specific message for edgefi.com
          if (wpApiUrl.toLowerCase().includes('edgefi.com')) {
            errorMessage = "Site edgefi.com appears to be returning HTML instead of JSON";
            details = "Based on our analysis, edgefi.com doesn't seem to have the WordPress REST API enabled. " +
                     "This could mean it's either not a WordPress site, or the REST API has been disabled by a security plugin. " +
                     "Try the suggested alternative URLs below or test the API endpoint directly.";
            
            // Generate URL suggestions specific to edgefi.com
            setSuggestedUrls(generateUrlSuggestions(wpApiUrl));
          } else {
            errorMessage = "The site returned HTML instead of JSON data";
            details = "This usually means the site is not a WordPress site with REST API enabled, or the API endpoint is redirecting to a regular webpage.";
            
            // Generate URL suggestions
            setSuggestedUrls(generateUrlSuggestions(wpApiUrl));
          }
        }
        
        // Try to extract more details if error is from our API
        try {
          // Some errors might contain JSON data in the message
          if (error.message.includes('{') && error.message.includes('}')) {
            const jsonStartIndex = error.message.indexOf('{');
            const jsonStr = error.message.substring(jsonStartIndex);
            const errorData = JSON.parse(jsonStr);
            
            if (errorData.details) {
              details = errorData.details;
            } else if (errorData.preview) {
              details = `Response preview: ${errorData.preview}`;
            }
            
            if (errorData.redirect_url) {
              details += `\nThe request was redirected to: ${errorData.redirect_url}`;
            }
          }
        } catch (parseError) {
          // Ignore JSON parsing errors
          console.log("Could not parse error details:", parseError);
        }
      }
      
      setImportError(errorMessage);
      setErrorDetails(details);
      
      toast({
        title: "Import Failed",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setImporting(false);
    }
  };

  // Test if the API endpoint is directly accessible
  const testApiEndpoint = async () => {
    try {
      // Construct the API URL
      let testUrl = wpApiUrl.trim();
      if (!testUrl.includes('/wp-json/')) {
        testUrl = `${testUrl}/wp-json/wp/v2/posts`;
      }
      
      // Add HTTPS if not present
      if (!testUrl.startsWith('https://')) {
        testUrl = `https://${testUrl.replace(/^http:\/\//, '')}`;
      }
      
      // Open in new tab
      window.open(testUrl, '_blank');
    } catch (error) {
      console.error("Error opening test URL:", error);
    }
  };
  
  // Try a suggested URL
  const handleTrySuggestedUrl = (url: string) => {
    setWpApiUrl(url);
    setActiveTab("import");
    // Don't auto-import, let the user review the URL first
    toast({
      title: "URL Updated",
      description: `The URL has been updated to ${url}. Click Import Posts to try this URL.`,
    });
  };

  // Check if the error indicates a non-WordPress site or redirection issue
  const isRedirectError = importError && 
    (importError.includes("HTML instead of JSON") || 
     errorDetails?.includes("login page") ||
     importError.includes("not valid JSON") ||
     importError.includes("returning HTML") ||
     importError.includes("appears to be returning HTML"));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Import WordPress Posts</CardTitle>
        <CardDescription>
          Fetch posts from WordPress site and import them into your blog
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="import">Import</TabsTrigger>
            <TabsTrigger value="troubleshooting">Troubleshooting</TabsTrigger>
          </TabsList>
          
          <TabsContent value="import" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="wp-api-url">WordPress Site URL</Label>
              <Input
                id="wp-api-url"
                value={wpApiUrl}
                onChange={(e) => setWpApiUrl(e.target.value)}
                placeholder="https://yourdomain.com"
                disabled={isLoading || importing}
              />
              <p className="text-sm text-gray-500">
                Enter your WordPress site URL (e.g., https://example.com)
              </p>
            </div>
            
            {importing && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-500">
                  <span>Import Progress</span>
                  <span>{progressPercent}%</span>
                </div>
                <Progress value={progressPercent} className="w-full" />
                <p className="text-sm text-gray-600">
                  {importProgress}
                </p>
              </div>
            )}
            
            {importError && (
              <Alert className="bg-red-50 border-red-200 text-red-800">
                <AlertCircle className="h-4 w-4 text-red-500" />
                <AlertDescription className="text-sm">
                  <strong>Error:</strong> {importError}
                  {errorDetails && (
                    <div className="mt-2 text-xs text-red-700">
                      {errorDetails}
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}
            
            {isRedirectError && (
              <Alert className="bg-amber-50 border-amber-200">
                <InfoIcon className="h-4 w-4 text-amber-500" />
                <AlertTitle>Troubleshooting Tip</AlertTitle>
                <AlertDescription className="text-sm">
                  <p>Please switch to the Troubleshooting tab for help resolving this issue.</p>
                </AlertDescription>
              </Alert>
            )}
          </TabsContent>
          
          <TabsContent value="troubleshooting" className="space-y-4">
            <Alert className="bg-blue-50 border-blue-200">
              <InfoIcon className="h-4 w-4 text-blue-500" />
              <AlertTitle>WordPress Import Troubleshooting</AlertTitle>
              <AlertDescription className="text-sm space-y-2">
                <p>If you're seeing an error about HTML instead of JSON, try these steps:</p>
                <ol className="list-decimal pl-5 space-y-1">
                  <li>Verify the site is running WordPress</li>
                  <li>Check if the WordPress REST API is enabled on the site</li>
                  <li>Try different URL variations:
                    <ul className="list-disc pl-5 mt-1">
                      <li>Remove "www" if present (e.g., use example.com instead of www.example.com)</li>
                      <li>Try adding "/blog" if the WordPress is installed in a subdirectory</li>
                    </ul>
                  </li>
                  <li>Try manually testing the API endpoint for your site by clicking the button below:</li>
                </ol>
                <div className="mt-2">
                  <Button 
                    variant="outline"
                    onClick={testApiEndpoint}
                    className="text-blue-600 border-blue-300 flex items-center gap-2"
                  >
                    <ExternalLink className="h-3 w-3" />
                    Test API endpoint in browser
                  </Button>
                </div>
                <p className="mt-2 text-sm">If the browser shows JSON data, the API is working and might be blocked by CORS. If it shows a webpage, the site might not have the WordPress API enabled.</p>
                
                {suggestedUrls.length > 0 && (
                  <div className="mt-4 border p-3 rounded-md bg-white">
                    <h4 className="text-sm font-medium mb-2 flex items-center gap-1.5">
                      <GitBranch className="h-3.5 w-3.5 text-blue-500" />
                      Try these alternative URLs:
                    </h4>
                    <div className="space-y-2">
                      {suggestedUrls.map((url, index) => (
                        <div key={index} className="flex items-center justify-between text-sm bg-slate-50 p-2 rounded">
                          <code className="text-xs text-slate-600">{url}</code>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="h-7 text-xs text-blue-600"
                            onClick={() => handleTrySuggestedUrl(url)}
                          >
                            Try this URL
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <Accordion type="single" collapsible className="mt-4">
                  <AccordionItem value="common-issues">
                    <AccordionTrigger className="text-sm font-medium">
                      Common Issues and Solutions
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="list-disc pl-5 text-sm space-y-2">
                        <li>
                          <strong>Login Required:</strong> Some WordPress sites require authentication to access the API. 
                          In this case, you'll need to use WordPress's export feature and import the XML file instead.
                        </li>
                        <li>
                          <strong>REST API Disabled:</strong> The site might have the REST API disabled by a security plugin.
                          This is common for security-conscious sites like edgefi.com. Check if the site has a standard RSS feed 
                          that might be accessible at <code>/feed</code> as an alternative.
                        </li>
                        <li>
                          <strong>Not WordPress:</strong> The site might not be using WordPress. Check the page source 
                          for references to WordPress or common WordPress themes.
                        </li>
                        <li>
                          <strong>Custom API Path:</strong> Some sites customize the API path. Look for documentation 
                          or contact the site administrator.
                        </li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                  
                  <AccordionItem value="site-check">
                    <AccordionTrigger className="text-sm font-medium">
                      How to check if a site uses WordPress
                    </AccordionTrigger>
                    <AccordionContent>
                      <ol className="list-decimal pl-5 text-sm space-y-2">
                        <li>
                          Visit <code>https://DOMAIN/wp-login.php</code> - if it's WordPress, this will show a login page
                        </li>
                        <li>
                          Check the page source for <code>wp-content</code> or <code>wp-includes</code> references
                        </li>
                        <li>
                          Look for <code>WordPress</code> in the page footer or meta tags
                        </li>
                        <li>
                          Try accessing <code>https://DOMAIN/wp-json</code> - WordPress sites with REST API enabled will show JSON
                        </li>
                        <li>
                          Use online tools like "What CMS" or "BuiltWith" to identify the content management system
                        </li>
                      </ol>
                    </AccordionContent>
                  </AccordionItem>
                  
                  {wpApiUrl.toLowerCase().includes('edgefi.com') && (
                    <AccordionItem value="edgefi-specific">
                      <AccordionTrigger className="text-sm font-medium">
                        About edgefi.com
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="text-sm space-y-2">
                          <p>
                            Based on our analysis of edgefi.com, it appears the site might not be using WordPress
                            or has disabled the REST API. Here are some specific observations:
                          </p>
                          <ul className="list-disc pl-5 space-y-1">
                            <li>No <code>/wp-json</code> endpoint is accessible</li>
                            <li>Requests are redirecting to the homepage instead of returning JSON</li>
                            <li>The site may be using a security plugin that blocks API access</li>
                          </ul>
                          <p className="mt-3">
                            If you're the owner of edgefi.com and want to enable the WordPress REST API,
                            check your security plugins or contact your website administrator.
                          </p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  )}
                </Accordion>
              </AlertDescription>
            </Alert>
            
            {importError && (
              <Alert className="bg-red-50 border-red-200 text-red-800">
                <AlertCircle className="h-4 w-4 text-red-500" />
                <AlertDescription className="text-sm">
                  <strong>Last Error:</strong> {importError}
                  {errorDetails && (
                    <div className="mt-2 text-xs text-red-700">
                      {errorDetails}
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button
          onClick={() => setActiveTab(activeTab === "import" ? "troubleshooting" : "import")}
          variant="outline"
        >
          {activeTab === "import" ? "View Troubleshooting" : "Back to Import"}
        </Button>
        
        <Button
          onClick={handleImport}
          disabled={isLoading || importing}
          className="bg-edgefi-purple hover:bg-purple-800"
        >
          {importing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Fetching Posts...
            </>
          ) : (
            "Import Posts"
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};
